
// Pure helpers for getting real UTF-8 text through the `dbf` npm package's
// DBF writer correctly (task section 10: "Brug eksplicit UTF-8 encoding").
//
// Audit finding (read dbf@0.2.0's own source, src/lib.js writeField()):
// it writes exactly one BYTE per JS string character via `str.charCodeAt(i)`
// - a single-byte (Latin-1/ISO-8859-1-style) writer, not a real UTF-8
// encoder. Feeding it a normal JS Unicode string would only happen to
// survive for characters already ≤ U+00FF (which covers Danish æøå/ÆØÅ by
// coincidence, since those ARE in the Latin-1 range) - but the resulting
// BYTES would be Latin-1-encoded, not UTF-8, which is wrong once a .cpg
// declares "UTF-8" (exactly the Ã¸-style mojibake the task warns against:
// declaring one encoding while the bytes are actually another).
//
// The fix used throughout this module: pre-encode every value to its real
// UTF-8 byte sequence via TextEncoder, then represent that byte sequence as
// a JS "byte string" (one JS character per UTF-8 byte, code points 0-255
// only) BEFORE handing it to dbf.structure(). dbf's naive per-character
// single-byte writer then reproduces the intended UTF-8 bytes exactly,
// because by construction each character IS one intended byte. This is a
// standard, correct technique for driving a byte-oriented API with UTF-8
// content - not a workaround that loses information.
export function toUtf8ByteString(value){
	const bytes = new TextEncoder().encode(value === null || value === undefined ? "" : String(value));
	let result = "";
	for(let i = 0; i < bytes.length; i++) result += String.fromCharCode(bytes[i]);
	return result;
}

// dbf@0.2.0's fieldsize.js hardcodes every "C" (string) field to a fixed
// 254-byte width (task section 11: "string lengths" - a real, confirmed
// format/library limitation, not something this project invented). A
// UTF-8 byte string longer than that is truncated - but truncating at an
// arbitrary byte boundary can land mid-character (a multi-byte UTF-8
// sequence cut in half), which would corrupt the LAST character instead of
// just dropping it. This backs off to the nearest character boundary
// instead, so truncation is well-formed UTF-8 even for enormously long values.
const DBF_STRING_FIELD_BYTES = 254;

export function truncateUtf8ByteStringSafely(byteString, maxBytes = DBF_STRING_FIELD_BYTES){
	if(byteString.length <= maxBytes) return byteString;
	let end = maxBytes;
	// A UTF-8 continuation byte has its top two bits as "10" (0x80-0xBF) -
	// back off while the byte at the cut point is a continuation byte, so
	// the cut lands on a lead byte (the true start of a character).
	while(end > 0 && (byteString.charCodeAt(end) & 0xC0) === 0x80) end--;
	return byteString.slice(0, end);
}

export function encodeDbfStringValue(value){
	return truncateUtf8ByteStringSafely(toUtf8ByteString(value));
}

// DBF/dbf@0.2.0 field NAMES are a fixed 10-byte slot (structure.js:
// `f.name.split('').slice(0, 10)`, one byte per character via charCodeAt) -
// unlike attribute VALUES (task section 8/10: never transliterate content),
// a field name has no per-field encoding of its own and a hard 10-byte
// technical ceiling, so this narrowly ASCII-sanitises just the identifier
// slot - not the data. Danish letters map to their closest ASCII form
// (æ->ae, ø->o, å->aa - the conventional Danish transliteration for
// identifiers, distinct from the values themselves which keep full Unicode
// via encodeDbfStringValue above); anything else non-ASCII becomes "_".
// Also truncates to 10 chars and de-duplicates collisions (task section 11:
// "field-name restrictions") - two different long/similar keys must never
// silently collide into the same column and overwrite each other's data.
const DANISH_FIELD_NAME_MAP = {"æ": "ae", "Æ": "AE", "ø": "o", "Ø": "O", "å": "aa", "Å": "AA"};

function asciiIdentifier(name){
	let s = String(name).replace(/[æÆøØåÅ]/g, (c) => DANISH_FIELD_NAME_MAP[c]);
	s = s.replace(/[^\x20-\x7E]/g, "_").replace(/[^A-Za-z0-9_]/g, "_").slice(0, 10);
	return s.length > 0 ? s : "FIELD";
}

export function sanitizeFieldNames(names){
	const used = new Set();
	const map = new Map();
	for(const name of names){
		let candidate = asciiIdentifier(name);
		let suffix = 1;
		while(used.has(candidate.toUpperCase())){
			const suffixStr = String(suffix++);
			candidate = asciiIdentifier(name).slice(0, 10 - suffixStr.length) + suffixStr;
		}
		used.add(candidate.toUpperCase());
		map.set(name, candidate);
	}
	return map;
}
